import React, { Fragment, useState } from "react";

import { Autocomplete } from "@material-ui/lab";
import {
  Box,
  Card,
  CardContent,
  CardHeader,
  Divider,
  InputAdornment,
  TextField,
  Typography,
  makeStyles,
} from "@material-ui/core";
import { Search } from "@material-ui/icons";
import { isWhitespaceOnly } from "@ds-developer/frontend";

import { ChangeReason, TestCentresProps } from "./types";
import TestCentresSkeleton from "./components/Skeleton";

const useStyles = makeStyles(theme => ({
  autocomplete: {
    marginTop: theme.spacing(3),
  },
  autocompleteInput: {
    maxWidth: "450px",
  },
}));

const TestCentres: React.FC<TestCentresProps> = ({
  loading,
  disabled,
  onValueChange = () => {},
  options = [],
  value = [],
  handleSearchClick,
}) => {
  const classes = useStyles();
  const [acValue, setAcValue] = useState("");
  const onInputChange = (
    event: React.ChangeEvent<unknown>,
    value: string,
    reason: ChangeReason,
  ) => {
    const isInput = (reason: ChangeReason) => reason === "input";
    const isClear = (reason: ChangeReason) => reason === "clear";
    const isEmptyOrWhitespace = (value: string) =>
      !value || isWhitespaceOnly(value);
    if (isInput(reason)) {
      setAcValue(value);
    }
    if (isClear(reason) || isEmptyOrWhitespace(value)) {
      setAcValue("");
    }
  };
  return loading ? (
    <TestCentresSkeleton />
  ) : (
    <Card data-test="test-centres">
      <CardHeader title="My test centres" />
      <Divider />
      <CardContent>
        <Typography variant="body1" data-test="test-centres-title">
          Search for test centres you cover in all your areas
        </Typography>
        <Autocomplete
          className={classes.autocomplete}
          disableClearable
          forcePopupIcon={false}
          id="test-centres-autocomplete"
          disabled={disabled}
          multiple
          onInputChange={onInputChange}
          onChange={(_event, newValues) => {
            onValueChange(newValues);
          }}
          options={options}
          open={options.length > 0 && acValue.length > 1}
          getOptionLabel={option => option.name}
          getOptionSelected={(option, value) => option.id === value.id}
          ListboxProps={{
            ["data-test"]: "test-centres-list",
          }}
          ChipProps={{
            ["data-test"]: "test-centres-chip",
          }}
          renderInput={params => (
            <Fragment>
              <TextField
                {...params}
                className={classes.autocompleteInput}
                placeholder="Search for a test centre"
                variant="outlined"
                InputProps={{
                  // We have to manually spread these InputProps otherwise the autocomplete won't work :/
                  ...params.InputProps,
                  startAdornment: (
                    <InputAdornment position="start">
                      <Search />
                    </InputAdornment>
                  ),
                  // @ts-ignore
                  ["data-test"]: "test-centres-input",
                }}
                onClick={handleSearchClick}
              />
              {/* These adornments are the 'selected' autocomplete entries */}
              <Box mt={2}>{params.InputProps.startAdornment}</Box>
            </Fragment>
          )}
          value={value}
        />
      </CardContent>
    </Card>
  );
};

export default TestCentres;
